import React, { useCallback, useEffect, useState } from "react"
import { ErrorBoundary } from "../ErrorBoundary"
import { useSyncPoll } from "../useSyncPoll"
import { TransactionsFilters } from "./TransactionsFilters"
import { TransactionsTable } from "./TransactionsTable"
import { SummaryBar } from "./SummaryBar"
import { Pagination } from "./Pagination"
import { ExportButton } from "./ExportButton"
import { SyncNowButton } from "./SyncNowButton"

const EMPTY_FILTERS = { type: "", app_id: "", shop_domain: "", from: "", to: "" }

// Transactions ledger (doc 05). Reads a paginated, filtered page from the index
// JSON and renders summary + filters + responsive table + pagination. `draft`
// holds in-progress filter edits; `applied` is what's actually queried, so the
// table doesn't refetch on every keystroke.
export default function TransactionsApp({
  transactionsPath,
  exportPath,
  appsPath,
  syncPath,
  detailBasePath,
  canExport,
  canSync,
  initialAppId,
  copy,
}) {
  // Seeded from the URL app_id (per-app nav link) so the page loads scoped to
  // that app; the user can still change or clear the App filter as normal.
  const seededFilters = { ...EMPTY_FILTERS, app_id: initialAppId || "" }
  const [draft, setDraft] = useState(seededFilters)
  const [applied, setApplied] = useState(seededFilters)
  const [page, setPage] = useState(1)
  const [data, setData] = useState(null)
  const [apps, setApps] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  // Bumped to force a ledger refetch (after a manual sync, while polling).
  const [reloadKey, setReloadKey] = useState(0)
  const { syncing, startSync } = useSyncPoll(data, setReloadKey)

  // App list feeds the filter dropdown (reuses the App Setup index JSON).
  useEffect(() => {
    let active = true
    fetch(appsPath, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((rows) => active && setApps(rows))
      .catch(() => {})
    return () => {
      active = false
    }
  }, [appsPath])

  const buildUrl = useCallback(() => {
    const params = new URLSearchParams()
    Object.entries(applied).forEach(([k, v]) => {
      if (v) params.set(k, v)
    })
    params.set("page", page)
    const sep = transactionsPath.includes("?") ? "&" : "?"
    return `${transactionsPath}${sep}${params}`
  }, [transactionsPath, applied, page])

  useEffect(() => {
    let active = true
    // Only show the skeleton on first load; refetches (filters, sync polling)
    // update in place so the table doesn't flash.
    setLoading((prev) => prev || data === null)
    setError(false)
    fetch(buildUrl(), { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((json) => active && setData(json))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [buildUrl, reloadKey])

  const applyFilters = () => {
    setApplied(draft)
    setPage(1)
  }
  const clearFilters = () => {
    setDraft(EMPTY_FILTERS)
    setApplied(EMPTY_FILTERS)
    setPage(1)
  }
  const applyPreset = (from, to) => {
    const next = { ...draft, from, to }
    setDraft(next)
    setApplied(next)
    setPage(1)
  }

  const header = (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{copy.title}</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{copy.subtitle}</p>
      </div>
      <div className="flex items-center gap-3">
        {canSync && (
          <SyncNowButton
            syncPath={syncPath}
            lastSyncedAt={data?.meta?.last_synced_at}
            syncing={syncing}
            onSyncStart={startSync}
            copy={copy}
          />
        )}
        {canExport && <ExportButton exportPath={exportPath} filters={applied} copy={copy} />}
      </div>
    </div>
  )

  const shell = (body) => (
    <ErrorBoundary>
      {header}
      <div className="mt-6 space-y-4">
        <TransactionsFilters
          draft={draft}
          setDraft={setDraft}
          onApply={applyFilters}
          onClear={clearFilters}
          onPreset={applyPreset}
          apps={apps}
          copy={copy}
        />
        {body}
      </div>
    </ErrorBoundary>
  )

  if (error) {
    return shell(
      <div className="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/40 p-6">
        <p className="text-sm text-gray-700 dark:text-gray-300">{copy.states.error}</p>
      </div>
    )
  }
  if (loading || !data) {
    return shell(<p className="text-sm text-gray-400 dark:text-gray-500">{copy.states.loading}</p>)
  }
  if (data.total === 0) {
    return shell(
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-10 text-center">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{copy.empty.title}</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{copy.empty.body}</p>
      </div>
    )
  }

  return shell(
    <>
      <SummaryBar totals={data.totals} total={data.total} copy={copy} />
      <TransactionsTable rows={data.rows} copy={copy} detailBasePath={detailBasePath} />
      <Pagination
        page={data.page}
        perPage={data.per_page}
        total={data.total}
        onPage={setPage}
        copy={copy}
      />
    </>
  )
}
